import { createApp, ref, onMounted, onBeforeMount, onBeforeUnmount } from 'vue';
import { createPinia } from 'pinia'
import { isMobile } from 'mobile-device-detect'
import { useAllisStore } from '../stores/allis'
import ShareBar from './components/ShareBar.vue'
import VimeoEmbed from './components/VimeoEmbed.vue'
import VimeoPlayer from './components/VimeoPlayer.vue'
import AllIs from './components/AllIs.vue'
import ClientLogos from './components/ClientLogos.vue'
import LatestListing from './components/LatestListing.vue'
import LatestFeatured from './components/LatestFeatured.vue'
import ButtonReadMore from './components/ButtonReadMore.vue'
import Toggler from './components/Toggler.vue'
import WorkMeta from './components/WorkMeta.vue'
import Accordion from './components/Accordion.vue'
import AccordionBio from './components/AccordionBio.vue'
import Directions from './components/Directions.vue'
import MouseFollower from './components/MouseFollower.vue'
import MagneticButton from './components/MagneticButton.vue'
import WonderlandMark from './components/WonderlandMark.vue'
import Swiper from './components/Swiper.vue'
import MailChimp from './components/MailChimp.vue'
import MailChimp7 from './components/MailChimp7.vue'
import MailChimpNewsletter7 from './components/MailChimpNewsletter7.vue'
// import WonderlandImages from './components/WonderlandImages.vue'
import WonderlandNew from './components/WonderlandNew.vue'
import TimeOut from './components/TimeOut.vue'

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollSmoother from "gsap/ScrollSmoother";
import SplitText from "gsap/SplitText";
import Vidle from 'v-idle-3'

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

const pinia = createPinia()

const app = createApp({
  data() {
    return {
      form: 'retail',
    }
  },
  setup() {
    const allis = useAllisStore(); 
    const showSubNav = ref(false);
    const menuOverlayStatus = ref(false);
    const wonderlandStatus = ref(false);
    const headerClass = ref('dark');
    const isScrolled = ref(false);
    const isBodyHome = ref(false);
    
    let sections = [];
    let lastScrollTop = 0;

    const openOverlayMenu = () => {
      menuOverlayStatus.value = true;
    };

    const closeOverlayMenu = () => {
      menuOverlayStatus.value = false;
    };

    const toggleOverlayMenu = () => {
      menuOverlayStatus.value = !menuOverlayStatus.value;
    };

    const toggleWonderland = () => {
      wonderlandStatus.value = !wonderlandStatus.value;
      if (!isMobile) {
        ScrollTrigger.normalizeScroll(!wonderlandStatus.value);
        const isNormalScroll = () => {
          return ScrollTrigger.normalizeScroll();
        };
        const currentValue = isNormalScroll();
        console.log('Is normal scroll:', currentValue);
      }
    };

    const setupGSAP = () => {

      // GSAP SplitText (hero headline)
      if (document.querySelector(".sp__lines")) {
        const splitLines = new SplitText(".sp__lines", {
          type: "lines",
          linesClass: "line line++"
        });
  
        gsap.from(splitLines.lines, {
          scrollTrigger: {
            trigger: ".sp__lines",
            start: "50% 60%",
            // markers: true,
            onComplete: splitRevert
          },
          yPercent: 100,
          ease: "power3",
          stagger: 0.2,
          duration: 1,
          delay: 0.25,
        });
        
        function splitRevert() {
          splitLines.revert();
        }
      }

      // GSAP ScrollTrigger for header
      ScrollTrigger.create({
        start: "top top",
        end: 99999,
        onUpdate: (self) => {
          const header = document.querySelector('.header');
          const shouldHideHeader = self.direction === 1;
          header.classList.toggle("hidden", shouldHideHeader);
        }
      });

      // GSAP ScrollSmoother
      ScrollSmoother.create({
        smooth: 1.7, // how long (in seconds) it takes to "catch up" to the native scroll position
        effects: true, // looks for data-speed and data-lag attributes on elements
        smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
        normalizeScroll: true,
      });

      // GSAP ScrollTrigger for section color change
      const sections = document.querySelectorAll('#smooth-content [data-color]');
      const header = document.querySelector('.header');    

      sections.forEach((section) => {
        gsap.to('.header', {
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: `+=${section.offsetHeight}`,
            // markers: true,
            onToggle: (self) => {
              headerClass.value = self.isActive
                ? section.getAttribute('data-color') || 'light'
                : headerClass.value;
            },
          },
        });
      });      
    };

    const homeGSAP = () => {
      let tl = gsap.timeline()

      tl.to("html", {"--home-line-sides": "100%", duration: .75, delay: .5});
      tl.to("html", {"--home-logo-position-x": "1.8rem", duration: 1}, .01);
      tl.to("html", {"--home-burger-position-x": "1.7rem", duration: 1}, .01);
      tl.to("html", {"--home-footer-top": "100%", duration: .75}, .65);
      tl.to("html", {"--home-logotype-position-y": "0%", duration: .5}, 1.05);
      tl.to("html", {"--home-footer-cta-work": "0%", duration: .5}, 1.05);
      tl.to("html", {"--home-footer-cta-shop": "0%", duration: .5}, 1.05);
      tl.to("html", {"--home-footer-copy": "0%", duration: .5}, 1.05);
      tl.to("html", {"--home-footer-left": "100%", duration: .5}, .75);
      tl.to("html", {"--home-footer-right": "100%", duration: .5}, 1.05);
      tl.to("html", {"--home-line-header": "100%", duration: .75}, 1.1);
      tl.to("html", {"--home-all-is-position-x": "0%", duration: 1}, .8);
      tl.to("html", {"--home-nav-1": "0%", duration: 1}, .8);
      tl.to("html", {"--home-nav-2": "0%", duration: 1}, .9);
      tl.to("html", {"--home-nav-3": "0%", duration: 1}, 1);
      tl.to("html", {"--home-nav-4": "0%", duration: 1}, 1.1);
      tl.to("html", {"--home-nav-5": "0%", duration: 1}, 1.2);
      tl.to("html", {"--home-wonderland-mark-x": "0.9rem", duration: .5}, 1.4);
    }

    const footerGSAP = () => {
      console.log('footer logo');
      gsap.to("html", {
        "--footer-logotype-position-y": "0%",
        duration: .75,
        scrollTrigger: {
          trigger: ".footer__logo",
          start: "top 75%",
          end: "75% 75%",
          scrub: false,
          // markers: {
          //   trigger: "#trigger-marker",
          //   start: "#start-marker",
          //   end: "#end-marker"
          // }
        }
      });
    }

    const onIdle = () => {
      allis.userIdle = true;
      // console.log("Idle hands are the devil's playthings");
    };
    
    onMounted(() => {      
      if (!isMobile) {
        const smoothContent = document.querySelector('#smooth-content');
        const firstSection = smoothContent.querySelector('[data-color]');
        headerClass.value = firstSection.getAttribute('data-color');
        setupGSAP();
        isBodyHome.value = document.body.classList.contains('home');
        if(isBodyHome.value) {
          homeGSAP();
        }
        footerGSAP();
      } else {
        window.addEventListener('scroll', handleScroll);
      }
    });

    const handleScroll = () => {
      if (isMobile) {
        isScrolled.value = window.scrollY > 0;
      } else {
        
      }
    };

    onBeforeUnmount(() => {
      if (!isMobile) {
        // Kill all ScrollTriggers when the component is unmounted
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    });
    return {
      allis,
      showSubNav,
      menuOverlayStatus,
      openOverlayMenu,
      closeOverlayMenu,
      toggleOverlayMenu,
      headerClass,
      isMobile,
      wonderlandStatus,
      toggleWonderland,
      isScrolled,
      handleScroll,
      onIdle,
    };
  } 

});

app.use(pinia)
app.use(Vidle)

app.component('ShareBar', ShareBar)
app.component('VimeoEmbed', VimeoEmbed)
app.component('VimeoPlayer', VimeoPlayer)
app.component('AllIs', AllIs)
app.component('ClientLogos', ClientLogos)
app.component('LatestListing', LatestListing)
app.component('LatestFeatured', LatestFeatured)
app.component('ButtonReadMore', ButtonReadMore)
app.component('Toggler', Toggler)
app.component('WorkMeta', WorkMeta)
app.component('Accordion', Accordion)
app.component('AccordionBio', AccordionBio)
app.component('Directions', Directions)
app.component('MouseFollower', MouseFollower)
app.component('MagneticButton', MagneticButton)
app.component('WonderlandMark', WonderlandMark)
app.component('Swiper', Swiper)
app.component('MailChimp7', MailChimp7)
app.component('MailChimpNewsletter7', MailChimpNewsletter7)
// app.component('WonderlandImages', WonderlandImages)
app.component('WonderlandNew', WonderlandNew)
app.component('TimeOut', TimeOut)

app.mount('#app')